import React from 'react'
import cx from 'classnames'
import styles from './CardMedia.module.scss'
import Image from 'next/image'
import { Col, Container, Row } from 'react-grid-system'
import { Button, Heading, Paragraph } from '~elements'
import { ImageProps, RichTextType } from '~types'
import { useRouter } from 'next/router'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useSmoothAnchorScroll } from 'utils/hooks/useSmoothAnchorScroll'
import { useWindowSize } from 'utils/hooks'

interface CardMediaProps {
  copy?: RichTextType
  ctaBtnUrl?: string
  ctaBtnText?: string
  ctaBtnIsTargetBlank?: boolean
  ctaBtnColor?: string
  hasMediaInRightColumn?: boolean
  headline?: string
  mediaColumn: Array<ImageProps>
  subheadline: string
  imgSubheadline: ImageProps
  anchorId: string
  readMoreField?: RichTextType
}

const CardMedia = ({
  copy,
  ctaBtnUrl,
  ctaBtnText,
  ctaBtnIsTargetBlank,
  ctaBtnColor,
  hasMediaInRightColumn,
  headline,
  mediaColumn,
  subheadline,
  anchorId,
  readMoreField
}: CardMediaProps) => {
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')

  const cardMediaStyles = cx([
    styles['card-media'],
    styles[`has-media-in-${hasMediaInRightColumn ? 'right' : 'left'}-col`]
  ])

  const { asPath } = useRouter()
  const [, hash] = (asPath as string).split('#')
  const { ref: cardRef, shouldSetId } = useSmoothAnchorScroll(anchorId)
  const { url: imageUrl = null, altText: imageAlt = null } = mediaColumn?.[0]?.image ?? {}

  const useMediaImageUrl =
    (ctaBtnText && ctaBtnText.toLowerCase() === 'view map') ||
    (ctaBtnUrl && ctaBtnUrl.startsWith('https://media.graphassets.com'))

  const buttonUrl = useMediaImageUrl && imageUrl ? imageUrl : ctaBtnUrl
  const textJustification = isBelowSm ? 'center' : 'left'

  return (
    <div className={cardMediaStyles} {...(shouldSetId && { id: anchorId })} ref={cardRef}>
      <Container className={'container container--narrow-xxl'}>
        <Row>
          <Col md={6}>
            <div className={styles['col-wrapper--media']}>
              {imageUrl && (
                <div className={styles['media-column']}>
                  <div className={'image-container'}>
                    <Image
                      alt={imageAlt || headline || ''}
                      src={imageUrl}
                      sizes='100%'
                      fill
                      quality={NEXT_IMAGE_QUALITY}
                    />
                  </div>
                </div>
              )}
            </div>
          </Col>
          <Col md={6} className='align-contents-vertical'>
            <div className={styles['col-wrapper--content']}>
              {headline && (
                <Heading level={2} justify={textJustification} noMarginTop={true}>
                  {headline}
                </Heading>
              )}
              {subheadline && (
                <Heading level={3} color='light' justify={textJustification} noMarginTop={true}>
                  {subheadline}
                </Heading>
              )}
              {copy && <Paragraph text={copy} fontSize='md' justify={textJustification} readMoreText={readMoreField} />}
              {ctaBtnText && ctaBtnUrl && (
                <Button
                  color={ctaBtnColor || 'light'}
                  fill='solid'
                  justify={textJustification}
                  title={ctaBtnText}
                  url={buttonUrl}
                  isTargetBlank={ctaBtnIsTargetBlank}
                  analytics={{
                    linkModule: 'card'
                  }}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default CardMedia
